import cookie from './cookie';
import { ToastType } from '../types/toast';

interface FlashMessage {
  type: ToastType;
  message: string;
}

/**
 * Reads the react_flash cookie, displays a toast, and clears the cookie.
 * Core logic for handling flash messages from backend.
 *
 * @param showToast - Function to display the toast message
 * @param duration - Optional duration for the toast (default: 3000ms)
 * @returns true if a flash message was found and displayed, false otherwise
 */
export function readAndDisplayFlashMessage(
  showToast: (message: string, type: ToastType, duration?: number) => void,
  duration: number = 3000,
): boolean {
  const flashCookie = cookie.get('react_flash');

  if (flashCookie) {
    try {
      // Cookie value is already URL-encoded by the backend, cookie.get decodes it
      const flash: FlashMessage = JSON.parse(flashCookie);

      if (flash.type && flash.message) {
        showToast(flash.message, flash.type, duration);
        // Clear the cookie after reading
        cookie.remove('react_flash', { path: '/' });
        return true;
      }
    } catch (e) {
      console.error('Failed to parse flash cookie:', e);
    }
  }
  return false;
}

/**
 * Checks for and displays flash message from backend cookie.
 * Used when fetch redirects don't trigger useFlashMessages hook.
 * Includes a small delay to ensure cookie is set after redirect.
 *
 * @param showToast - Function to display the toast message
 * @param delay - Optional delay in milliseconds before reading the cookie (default: 100ms)
 * @param duration - Optional duration for the toast (default: 3000ms)
 */
export function showFlashMessage(
  showToast: (message: string, type: ToastType, duration?: number) => void,
  delay: number = 100,
  duration: number = 3000,
): void {
  setTimeout(() => {
    readAndDisplayFlashMessage(showToast, duration);
  }, delay);
}
